import { ResolveBlock } from '@bechtel/client-blocks';
import {
  SuspenseLoader,
  bankersRound,
  getDecimalPlaces,
  getUserUnitLabel,
  regularRound,
  useAppUser,
  useBlockPermission,
} from '@bechtel/client-common';
import {
  Box,
  ProgressBarDetail,
  ProgressBarDetailSpacing,
  ProgressBarSize,
  SetSpacing,
  Stat,
  Text,
  useSideSheetState,
} from '@bechtel/gds';
import { CrewMemberAssignment, MaterialOnSite, MaterialTrackingCode, Quantity, WorkPackage } from '@bechtel/types';
import { t } from '@lingui/macro';
import { concat, filter, head, orderBy, some } from 'lodash';
import { FC, Suspense } from 'react';
import { WorkPackageQuantityStats, WorkPackageQuantityStatsSideSheet } from '~/components';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { ProgressBarContainerStyled, StatsBlockStyled, StatsContainerStyled, StatsListStyled } from './styled';

interface StatListProps {
  numBlocks: number;
  hoursEarned: number;
  totalEarnableHours: number;
  materialOnsite?: MaterialOnSite;
  workPackage: WorkPackage;
  quantityEarned: number;
  totalEarnableQuantity: Quantity;
  isUnitOfMeasureHour: boolean;
}

const StatList: FC<StatListProps> = ({
  numBlocks,
  hoursEarned,
  totalEarnableHours,
  materialOnsite,
  workPackage,
  quantityEarned,
  totalEarnableQuantity,
  isUnitOfMeasureHour,
}) => {
  const { user } = useAppUser();
  const { visible, open, close } = useSideSheetState(false);
  const assignedCrewMembers = concat(workPackage.primaryOwners, workPackage.secondaryOwners, workPackage.otherOwners);
  const { canResolveBlock } = useBlockPermission(
    assignedCrewMembers,
    workPackage.facilities ? workPackage.facilities : []
  );
  const {
    visible: quantityDetailsVisible,
    open: quantityDetailsOpen,
    close: quantityDetailClose,
  } = useSideSheetState(false);

  return (
    <StatsListStyled>
      <Box flexBasis={{ sm: materialOnsite ? '33%' : '50%' }} justifyContent={{ sm: 'center' }} py={{ sm: '300' }}>
        {isUnitOfMeasureHour && (
          <WorkPackageQuantityStats
            label={t`Hours Earned vs Total Hours`}
            value={`${bankersRound(hoursEarned, 0)}/${bankersRound(totalEarnableHours, 0)}`}
            onClick={() => {
              quantityDetailsOpen();
            }}
          />
        )}{' '}
        {!isUnitOfMeasureHour && (
          <WorkPackageQuantityStats
            label={`${getUserUnitLabel(user)} Earned vs Total ${getUserUnitLabel(user)}`}
            value={(() => {
              const decimalPlaces = getDecimalPlaces(totalEarnableQuantity.quantity);
              return `${regularRound(quantityEarned, decimalPlaces)}/${totalEarnableQuantity.quantity}`;
            })()}
            onClick={() => {
              quantityDetailsOpen();
            }}
          />
        )}
      </Box>
      {quantityDetailsVisible && (
        <WorkPackageQuantityStatsSideSheet
          workPackageId={workPackage.id}
          isUnitOfMeasureHour={isUnitOfMeasureHour}
          onClose={quantityDetailClose}
        />
      )}
      {materialOnsite && (
        <Box flexBasis={{ sm: '33%' }} justifyContent={{ sm: 'center' }} py={{ sm: '300' }}>
          <Stat
            label={`${materialOnsite.commodityType.name}${t`(s) on Site`}`}
            // TODO: implement numOnSite value once DSC data is available
            // value={`${materialOnsite.numOnSite}/${materialOnsite.totalCommodities}`}
            value={`--/${materialOnsite.totalCommodities}`}
            spacing={SetSpacing.MEDIUM}
          />
        </Box>
      )}
      {numBlocks > 0 ? (
        <>
          <Box
            style={{ cursor: canResolveBlock ? 'pointer' : 'auto' }}
            flexBasis={{ sm: materialOnsite ? '33%' : '50%' }}
            justifyContent={{ sm: 'center' }}
            onClick={() => {
              if (canResolveBlock) open();
            }}
          >
            <Stat label={t`Blocks`} value={numBlocks} spacing={SetSpacing.MEDIUM} />
          </Box>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            {visible && (
              <ResolveBlock
                workPackageId={workPackage.id}
                workPackageTag={workPackage.tag}
                facilities={workPackage.facilities}
                onClose={close}
                assignedCrewMembers={assignedCrewMembers}
              />
            )}
          </Suspense>
        </>
      ) : (
        <Box flexBasis={{ sm: materialOnsite ? '33%' : '50%' }} justifyContent={{ sm: 'center' }} py={{ sm: '300' }}>
          <Stat label={t`Blocks`} value={numBlocks} spacing={SetSpacing.COMPACT} />
        </Box>
      )}
    </StatsListStyled>
  );
};

interface WorkPackageStatsProps {
  workPackage: WorkPackage;
  isUnitOfMeasureHour: boolean;
}

const getMaterialsOnsite = (materialOnsiteList: MaterialOnSite[] | null | undefined) => {
  // NOTE: we only want to use MaterialTrackingCode.MATERIAL_COMMODITIES since those contain the onsite vs. offsite numbers
  const filteredList = filter(materialOnsiteList, {
    materialTracking: MaterialTrackingCode.MATERIAL_COMMODITIES,
  });

  // TODO: see if we can get backend to send this list pre-ordered
  const orderedList = orderBy(filteredList, ['totalCommodities'], ['desc']);

  return head(orderedList);
};

const isAnyOwnerAssigned = (crewMemberAssignments: CrewMemberAssignment[]): boolean => {
  return some(crewMemberAssignments, (assignment) => Boolean(assignment.crewMember));
};

export const WorkPackageStats: FC<WorkPackageStatsProps> = ({ workPackage, isUnitOfMeasureHour }) => {
  const hasBlocks = workPackage.numBlocks > 0;
  const materialOnsite = getMaterialsOnsite(workPackage.materialsOnSite);
  const isSecondaryOwnersAssigned = isAnyOwnerAssigned([...workPackage.secondaryOwners, ...workPackage.otherOwners]);
  const { canEditForeman, canEditGeneralForeman, canEditSuperintendent, canEditFieldEngineer } =
    useWorkPackagePermissions([
      ...workPackage.primaryOwners,
      ...workPackage.secondaryOwners,
      ...workPackage.otherOwners,
    ]);
  const canEditOwnership = canEditForeman || canEditGeneralForeman || canEditSuperintendent || canEditFieldEngineer;
  const percentageComplete = Math.round((workPackage.percentageComplete ?? 0) * 100) / 100;
  //TODO: change to function component once changes done in gds for props
  const progressBarLeftLabel: FC = () => {
    return (
      <Text size="300" color="grey.600">
        {hasBlocks ? `${workPackage.numBlocks} ${t`Blocked Milestones`}` : t`In Progress`}&nbsp;-&nbsp;
        <Text size="300" color="grey.600" weight="600">{`${percentageComplete}% ${t`Complete`}`}</Text>
      </Text>
    );
  };

  return (
    <StatsContainerStyled>
      <ProgressBarContainerStyled
        isSecondaryOwnersAssigned={isSecondaryOwnersAssigned}
        canEditOwnership={canEditOwnership}
      >
        <ProgressBarDetail
          size={ProgressBarSize.MEDIUM}
          currentValue={workPackage.earnedHours}
          totalValue={workPackage.totalEarnableHours}
          usePercentage={true}
          percentage={percentageComplete}
          fillPattern={hasBlocks}
          spacing={ProgressBarDetailSpacing.MEDIUM}
          leftLabel={progressBarLeftLabel}
          data-testid="wp-progress-bar"
        />
      </ProgressBarContainerStyled>

      <StatsBlockStyled>
        <StatList
          numBlocks={workPackage.numBlocks}
          hoursEarned={workPackage.earnedHours}
          materialOnsite={materialOnsite}
          workPackage={workPackage}
          totalEarnableHours={workPackage.totalEarnableHours || 0}
          quantityEarned={workPackage.earnedQuantity}
          totalEarnableQuantity={workPackage.totalEarnableQuantity}
          isUnitOfMeasureHour={isUnitOfMeasureHour}
        />
      </StatsBlockStyled>
    </StatsContainerStyled>
  );
};
